// Mock data for interactive onboarding demo

export interface MockBusiness {
  name: string;
  address: string;
  phone: string;
  website: string;
  rating: number;
  totalReviews: number;
  businessTypes: string[];
  openingHours: string;
}

export interface MockEmployee {
  name: string;
  role: string;
  color: string;
}

export interface HourlyOrder {
  hour: string;
  orders: number;
}

export interface MockBusyness {
  hourlyOrders: HourlyOrder[];
  dailyRevenue: number;
  ordersToday: number;
  averageOrder: number;
  topProduct: string;
}

export interface MockTable {
  id: number;
  status: "available" | "occupied" | "reserved";
  seats: number;
  revenue?: string;
  time?: string;
}

export interface TableStats {
  occupancy: string;
  avgTurnover: string;
  revenuePerTable: string;
}

export interface MockDashboard {
  latestReview: {
    rating: number;
    text: string;
    author: string;
  };
  sentiment: {
    positive: number;
    neutral: number;
    negative: number;
  };
  avgRating: number;
  totalReviews: number;
}

// Step 3: Feature Preview Interfaces
export interface MockReviewsPreview {
  latestReview: {
    rating: number;
    author: string;
    text: string;
    source: string;
    timeAgo: string;
  };
  avgRating: number;
  totalReviews: number;
  sentimentPositive: number;
}

export interface MockEmployeesPreview {
  currentTime: string;
  businessStatus: string;
  scheduledCount: number;
  presentCount: number;
  statusBreakdown: {
    onTime: number;
    early: number;
    late: number;
    noShow: number;
  };
  overallStatus: "optimal" | "understaffed" | "issues" | "overstaffed";
}

export interface MockLoyaltyPreview {
  businessName: string;
  memberName: string;
  pointsBalance: number;
  memberSince: string;
  passColor: string;
}

export interface MockEmailsPreview {
  latestCampaign: {
    subject: string;
    preview: string;
    sentTo: number;
    openRate: number;
    clickRate: number;
    sentAt: string;
  };
}

export interface MockPayrollPreview {
  employee: {
    name: string;
    initials: string;
    role: string;
    hourlyRate: string;
  };
  hours: string;
  gross: string;
  net: string;
  status: "paid" | "pending" | "processing";
}

export interface MockSchedulingPreview {
  shifts: {
    employee: string;
    initials: string;
    color: string;
    startPercent: number;
    widthPercent: number;
    time: string;
  }[];
  timeRange: string;
  currentTimePercent: number;
}

// Step 1: Google Maps Business Data
export const MOCK_BUSINESS: MockBusiness = {
  name: "Café Bratislava",
  address: "Hlavné námestie 1, 811 01 Bratislava",
  phone: "+421 2 5555 1234",
  website: "cafebratislava.sk",
  rating: 4.7,
  totalReviews: 342,
  businessTypes: ["Restaurant", "Café", "Bar"],
  openingHours: "Mon-Fri: 8:00-22:00, Sat-Sun: 9:00-23:00",
};

// Step 2: Dotypos Employee Data
export const MOCK_EMPLOYEES: MockEmployee[] = [
  { name: "Jan Novák", role: "Manager", color: "#4F46E5" },
  { name: "Eva Horváthová", role: "Barista", color: "#EC4899" },
  { name: "Peter Szabó", role: "Chef", color: "#10B981" },
  { name: "Anna Kováčová", role: "Server", color: "#F59E0B" },
  { name: "Martin Tóth", role: "Bartender", color: "#8B5CF6" },
  { name: "Lucia Vargová", role: "Server", color: "#06B6D4" },
  { name: "Tomáš Horák", role: "Cook", color: "#EF4444" },
  { name: "Katarína Bílá", role: "Host", color: "#14B8A6" },
  { name: "Jakub Černý", role: "Server", color: "#A855F7" },
  { name: "Mária Veselá", role: "Barista", color: "#F97316" },
];

// Step 2: Dotypos Busyness Analytics
export const MOCK_BUSYNESS: MockBusyness = {
  hourlyOrders: [
    { hour: "8:00", orders: 15 },
    { hour: "12:00", orders: 42 }, // Peak lunch
    { hour: "16:00", orders: 28 },
    { hour: "20:00", orders: 38 }, // Peak dinner
  ],
  dailyRevenue: 1847,
  ordersToday: 127,
  averageOrder: 14.5,
  topProduct: "Cappuccino",
};

// Step 2: Restaurant Table Layout
export const MOCK_TABLES: MockTable[] = [
  { id: 1, status: "available", seats: 2 },
  { id: 2, status: "occupied", seats: 4, revenue: "€42" },
  { id: 3, status: "reserved", seats: 6, time: "19:30" },
  { id: 4, status: "available", seats: 2 },
  { id: 5, status: "occupied", seats: 4, revenue: "€28" },
  { id: 6, status: "available", seats: 2 },
  { id: 7, status: "occupied", seats: 8, revenue: "€67" },
  { id: 8, status: "reserved", seats: 4, time: "20:00" },
  { id: 9, status: "available", seats: 2 },
];

export const TABLE_STATS: TableStats = {
  occupancy: "68%",
  avgTurnover: "1.5 hours",
  revenuePerTable: "€28",
};

// Step 3: Dashboard Preview Data
export const MOCK_DASHBOARD: MockDashboard = {
  latestReview: {
    rating: 5,
    text: "Excellent service and amazing coffee! The atmosphere is perfect for working or meeting friends.",
    author: "Martin K.",
  },
  sentiment: {
    positive: 78,
    neutral: 18,
    negative: 4,
  },
  avgRating: 4.7,
  totalReviews: 342,
};

// Step 3: Feature Preview Data (matching MOCK_BUSINESS data where applicable)
export const MOCK_REVIEWS_PREVIEW: MockReviewsPreview = {
  latestReview: {
    rating: 5,
    author: "Martin K.",
    text: "Excellent service and amazing coffee! The atmosphere is perfect.",
    source: "Google",
    timeAgo: "2 hours ago",
  },
  avgRating: 4.7,
  totalReviews: 342,
  sentimentPositive: 78,
};

export const MOCK_EMPLOYEES_PREVIEW: MockEmployeesPreview = {
  currentTime: "14:32",
  businessStatus: "Open",
  scheduledCount: 8,
  presentCount: 7,
  statusBreakdown: {
    onTime: 5,
    early: 1,
    late: 1,
    noShow: 0,
  },
  overallStatus: "optimal",
};

export const MOCK_LOYALTY_PREVIEW: MockLoyaltyPreview = {
  businessName: "Café Bratislava",
  memberName: "Anna Kováčová",
  pointsBalance: 340,
  memberSince: "Oct 2024",
  passColor: "#8B5CF6",
};

export const MOCK_EMAILS_PREVIEW: MockEmailsPreview = {
  latestCampaign: {
    subject: "Holiday Special: 20% Off!",
    preview: "Dear customer, enjoy our special holiday discount...",
    sentTo: 156,
    openRate: 68,
    clickRate: 24,
    sentAt: "Yesterday",
  },
};

export const MOCK_PAYROLL_PREVIEW: MockPayrollPreview = {
  employee: {
    name: "Eva Horváthová",
    initials: "EH",
    role: "Barista",
    hourlyRate: "€12/hr",
  },
  hours: "28.5h",
  gross: "€342",
  net: "€285",
  status: "pending",
};

export const MOCK_SCHEDULING_PREVIEW: MockSchedulingPreview = {
  shifts: [
    { employee: "Jan N.", initials: "JN", color: "#4F46E5", startPercent: 0, widthPercent: 50, time: "8:00-14:00" },
    { employee: "Eva H.", initials: "EH", color: "#EC4899", startPercent: 25, widthPercent: 50, time: "12:00-18:00" },
    { employee: "Peter S.", initials: "PS", color: "#10B981", startPercent: 50, widthPercent: 50, time: "14:00-20:00" },
  ],
  timeRange: "8:00 - 20:00",
  currentTimePercent: 55,
};
